import type { BadgeVariant } from '@/components/ui/badge'
import { TransactionType } from '@/lib/enums/TransactionType'

/** A transaction, or a report row that names its type in `transaction_type`. */
type TransactionDisplaySource = {
  type?: string | null
  transaction_type?: string | null
  sales_order_id?: number | null
}

export type TransactionDisplay = {
  label: string
  badgeVariant: BadgeVariant
  amountColor: string
}

function formatFallbackTransactionLabel(type: string | null | undefined) {
  return type?.replaceAll('_', ' ') || 'Unknown'
}

export function getTransactionDisplay(transaction: TransactionDisplaySource): TransactionDisplay {
  const type = transaction.type ?? transaction.transaction_type
  const hasSalesOrder = Boolean(transaction.sales_order_id)

  if (type === TransactionType.Adjustment.Value && hasSalesOrder) {
    return {
      label: 'Order Processed',
      badgeVariant: 'success',
      amountColor: 'text-status-success-foreground dark:text-status-success-foreground',
    }
  }

  if (type === TransactionType.ReturnAdjustment.Value && hasSalesOrder) {
    return {
      label: 'Order Refunded',
      badgeVariant: 'destructive',
      amountColor: 'text-status-destructive-foreground dark:text-status-destructive-foreground',
    }
  }

  if (type === TransactionType.Payment.Value && hasSalesOrder) {
    return {
      label: 'Payment Collected',
      badgeVariant: 'success',
      amountColor: 'text-status-success-foreground dark:text-status-success-foreground',
    }
  }

  if (type === TransactionType.ReturnPayment.Value && hasSalesOrder) {
    return {
      label: 'Payment Refunded',
      badgeVariant: 'destructive',
      amountColor: 'text-status-destructive-foreground dark:text-status-destructive-foreground',
    }
  }

  if (type === TransactionType.DepositApplication.Value) {
    return {
      label: 'Deposit Applied',
      badgeVariant: 'secondary',
      amountColor: 'text-muted-foreground',
    }
  }

  if (type === TransactionType.FeeReturn.Value || type === TransactionType.RefundReversal.Value) {
    return {
      label: type === TransactionType.FeeReturn.Value ? 'Fees Returned' : 'Refund Reversed',
      badgeVariant: 'success',
      amountColor: 'text-status-success-foreground dark:text-status-success-foreground',
    }
  }

  if (
    type === TransactionType.Adjustment.Value ||
    type === TransactionType.ReturnAdjustment.Value
  ) {
    return {
      label: 'Other',
      badgeVariant: 'warning',
      amountColor: 'text-status-warning-foreground dark:text-status-warning-foreground',
    }
  }

  return {
    label:
      type === TransactionType.SalesOrder.Value ? 'Order' : formatFallbackTransactionLabel(type),
    badgeVariant: 'secondary',
    amountColor: 'text-muted-foreground',
  }
}
